import { TooltipPlacement } from 'antd/es/tooltip';
import { isEqual } from 'lodash';
import React, { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Team } from 'api/models';
import { AssigneeValue } from 'api/tasks';
import { RootState } from 'data';
import { getActiveTemplatePlaceholders } from 'data/templates/projects/selectors';
import { useGetAllNativeUsersMap } from 'hooks/use-idb';
import { useTranslate } from 'hooks/use-translation';
import { teamOption, userOption } from '../squad-picker/utils';
import { AssigneePicker, BaseProps } from './assignee-picker';
// eslint-disable-next-line import/no-internal-modules

export type TemplateAssigneePickerProps = Omit<BaseProps, 'value'> & {
  value?: AssigneeValue;
  placement?: TooltipPlacement;
};

export const TemplateAssigneePicker: FC<TemplateAssigneePickerProps> = (
  props,
) => {
  const translate = useTranslate();
  const {
    selectProps,
    isPrivate,
    onDone,
    value,
    onPlaceholderCreate,
    placement,
  } = props;
  const { byId: teamsById } = useSelector(({ teams }: RootState) => teams);
  const templatePlaceholders = useSelector(getActiveTemplatePlaceholders());

  const filteredTeamsById: Record<number, Team> = useMemo(
    () =>
      Object.keys(teamsById).reduce(
        (result, key) => ({
          ...result,
          ...(teamsById[parseInt(key, 10)].accountLevelTeam
            ? {
                [parseInt(key, 10)]: teamsById[parseInt(key, 10)],
              }
            : {}),
        }),
        {},
      ),
    [teamsById],
  );

  const placeholders = useMemo(
    () => ({
      ...filteredTeamsById,
      ...templatePlaceholders,
    }),
    [filteredTeamsById, templatePlaceholders],
  );

  const activeUsersMap = useGetAllNativeUsersMap();

  const options = [
    {
      id: 'teams',
      label: translate('placeholders'),
      options: Object.values(placeholders)
        .filter((p) => p.teamType === 'NATIVE' || !isPrivate)
        .map(teamOption),
    },
    {
      id: 'users',
      label: translate('users'),
      options: Object.values(activeUsersMap)
        .filter((u) => u.userType === 'NATIVE' || !isPrivate)
        .map((_u) => userOption(_u)),
    },
  ];

  const _value = useMemo(() => {
    return [
      ...(value?.users || []).map((u) =>
        userOption(activeUsersMap[u.userId] ?? u),
      ),
      ...(value?.teams || []).map((t) =>
        teamOption(placeholders[t.teamId] ?? t),
      ),
    ];
  }, [activeUsersMap, placeholders, value?.teams, value?.users]);

  const _onDone = useCallback(
    (selected) => {
      if (isEqual(_value, selected)) {
        return;
      }
      const formatted = selected?.reduce(
        (result, option) => {
          if (option.extra.type === 'user') {
            return {
              ...result,
              users: [...result.users, { userId: option.extra.id }],
            };
          }

          return {
            ...result,
            teams: [...result.teams, { teamId: option.extra.id }],
          };
        },
        { users: [], teams: [] },
      );

      onDone?.(formatted);
    },
    [_value, onDone],
  );

  return (
    <AssigneePicker
      {...props}
      hidePlaceholderSection
      selectProps={{
        ...selectProps,
        options,
      }}
      popoverProps={{
        placement: placement || 'right',
      }}
      onDone={_onDone}
      value={_value}
      onPlaceholderCreate={onPlaceholderCreate}
      placeholderCreateOptions={{
        setCustomerRole: true,
      }}
    />
  );
};
